/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useEffect, useLayoutEffect, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@marriott/mi-ui-library';
import { PageParamsContext } from '../../../../modules/context/PageContext';
import { AEMInternetBarProps, InternetBarExperienceProps, InternetBarStateTypes } from '../../InternetBar.types';
import { inStayMockResponse } from '../../__mock__/InternetBar.model';
import { INTERNET_BAR_INITIAL_STATE } from '../../utils/constant';
import { getInternetBarStateSpecificClasses, StateCSS } from '../../internetBar.helper';
import { updateDataLayerProperties, sanitize } from '../../../../modules/utils/common';
import { globalDivisionList, internetBarIcons } from '../../../../modules/utils/constants/constants';
import { serviceStore, useServiceStore } from '../../../../modules/store/serviceStore';
import { useRouter } from 'next/router';
import { useSession } from '@marriott/mi-headless-utils';
import {
  connectBtnTrigger,
  getEnableSkipEnrollmentVerbiage,
  getEnrolledUserFlag,
  getLoyaltyEnabledForConferenceZone,
} from '../../utils/handler';

export const InternetBarAgeExperience: FC<InternetBarExperienceProps> = (pageProps: InternetBarExperienceProps) => {
  const [loyaltyConnectScenarioTextLabel, setLoyaltyConnectScenarioTextLabel] = useState<string>(
    pageProps?.loyaltyConnectConferenceTitle
  );
  const [loyaltyConnectScenarioDescriptionLabel, setLoyaltyConnectScenarioDescriptionLabel] = useState<string>(
    pageProps?.loyaltyConnectConferenceDescription
  );
  const { preProcessorResponse } = useContext(PageParamsContext);

  const {
    experienceSegment = '',
    state = '',
    userSegment = '',
    enableLoyalty = false as boolean,
    enableLoyaltyConference = false as boolean,
    loyaltyFlip = false,
    enrollmentSourceCodeMapping,
    propertyInfo,
    enableLoyaltyPostConnect,
    enableLoyaltyConferencePostConnect,
  } = preProcessorResponse || {};

  const serviceData = useServiceStore((state: serviceStore) => state.serviceData);

  const globalDivision = propertyInfo?.data?.basicInformation?.globalDivision;

  const isLoyaltyEnabledForConference = getLoyaltyEnabledForConferenceZone(
    enableLoyaltyConference ? enrollmentSourceCodeMapping?.loyalty : enrollmentSourceCodeMapping?.nonloyalty,
    globalDivision,
    propertyInfo?.data?.contactInformation?.address?.country?.code
  );

  const enableSkipEnrollmentVerbiage = getEnableSkipEnrollmentVerbiage(
    enableLoyalty ||
      enableLoyaltyPostConnect ||
      ((enableLoyaltyConference || enableLoyaltyConferencePostConnect) && isLoyaltyEnabledForConference)
      ? enrollmentSourceCodeMapping?.loyalty
      : enrollmentSourceCodeMapping?.nonloyalty,
    propertyInfo?.data?.basicInformation?.globalDivision,
    propertyInfo?.data?.contactInformation?.address?.country?.code
  );

  const {
    currentStateMapping = {
      ...(pageProps?.isAuthorMode ? inStayMockResponse : (INTERNET_BAR_INITIAL_STATE as InternetBarStateTypes)),
    },
  } = pageProps;
  const { isACDLenabled } = useSession();
  const router = useRouter();
  const urlQueryParams: Record<string, any> = router.query;

  const stateCSS: StateCSS = getInternetBarStateSpecificClasses(userSegment, state, experienceSegment, enableLoyalty);

  const [isNoLoginInterfaceModal, setIsNoLoginInterfaceModal] = useState<boolean>(
    currentStateMapping?.noLoginInterfaceModal ?? false
  );

  const [showIcon] = useState<boolean>(
    internetBarIcons && internetBarIcons?.[state] ? internetBarIcons?.[state]?.showIcon : false
  );

  const termsAndConditionsText = sanitize(
    (
      currentStateMapping?.termsAndConditions &&
      pageProps?.[currentStateMapping?.termsAndConditions as keyof AEMInternetBarProps]
    )?.toString() || ''
  );

  const isWelcomeTitle: boolean = currentStateMapping && currentStateMapping?.title === 'welcomeTitle';

  const [loyaltyJoinNow, setLoyaltyJoinNow] = useState<string>(pageProps?.joinButtonLink);
  const [isLoyaltyConnectNowBtnLoading, setLoyaltyConnectNowBtnLoading] = useState<boolean>(false);

  useEffect(() => {
    if (pageProps && pageProps?.internetPricingData) {
      const loyaltyEnrollmentSourceCode = pageProps?.internetPricingData?.['data']?.enrollmentSourceCode
        ? pageProps?.internetPricingData?.['data']?.enrollmentSourceCode
        : pageProps?.internetBarCurrentStateData['loyaltyEnrollmentSourceCode'];
      const joinNowLink = pageProps?.handleLinks(loyaltyEnrollmentSourceCode);
      setLoyaltyJoinNow(joinNowLink);
    } else if (
      pageProps?.internetBarCurrentStateData &&
      pageProps?.internetBarCurrentStateData?.['loyaltyEnrollmentSourceCode']
    ) {
      const { enrollmentSourceCode = '' } = pageProps?.internetPricingData?.data ?? {};
      const joinNowLink = pageProps?.handleLinks(
        enrollmentSourceCode
          ? enrollmentSourceCode
          : pageProps?.internetBarCurrentStateData?.['loyaltyEnrollmentSourceCode']
      );
      setLoyaltyJoinNow(joinNowLink);
    }
  }, [pageProps]);

  useEffect(() => {
    const { internetPricingData } = pageProps;
    const internetbarInitialValue = { ...pageProps?.internetBarCurrentStateData };
    const enrollmentSourceCode = internetPricingData?.['data']?.enrollmentSourceCode
      ? internetPricingData?.['data']?.enrollmentSourceCode
      : internetbarInitialValue?.['loyaltyEnrollmentSourceCode'];

    const { userExperience = '' } = pageProps?.internetPricingData?.data ?? {};
    if (!isACDLenabled) {
      const dataLayer = (window && window?.dataLayer) ?? {};
      updateDataLayerProperties(dataLayer, userExperience, enrollmentSourceCode);
    }
  }, [pageProps?.internetPricingData?.data, isACDLenabled]);

  useEffect(() => {
    if (enableSkipEnrollmentVerbiage) {
      setLoyaltyConnectScenarioTextLabel(pageProps?.loyaltyConnectScenarioAPECText);
      setLoyaltyConnectScenarioDescriptionLabel(pageProps?.loyaltyConnectScenarioAPECDescription);
    }
  }, [enableSkipEnrollmentVerbiage]);

  useLayoutEffect(() => {
    pageProps?.resizeImage && pageProps?.resizeImage();
  });

  const handleBtnClick = (btnName: string) => {
    const { setCurrentStateHasModalOption, setModalType, setShowInternetBarModal, internetPricingData } = pageProps;
    const internetbarInitialValue = { ...pageProps?.internetBarCurrentStateData };
    const zoneValue = internetbarInitialValue?.['Z'] ?? '';
    const localRequirement = internetbarInitialValue?.['LR']?.toUpperCase() ?? '';
    const ppv5Enrollment =
      internetbarInitialValue['ppv5Enrollment'] ?? urlQueryParams?.['ppv5Enrollment']?.toLowerCase() === 'true';
    const loyaltyEnrollmentSourceCode = internetPricingData?.['data']?.enrollmentSourceCode
      ? internetPricingData?.['data']?.enrollmentSourceCode
      : internetbarInitialValue?.['loyaltyEnrollmentSourceCode'];
    const isUserEnrolled: boolean = getEnrolledUserFlag(loyaltyEnrollmentSourceCode);

    switch (btnName) {
      case 'connectnow':
        if (zoneValue?.toUpperCase() === 'GUEST') {
          const { Z = '', zoneMapping } = pageProps?.internetBarCurrentStateData || {};
          if (
            globalDivision &&
            globalDivision === globalDivisionList?.APEC &&
            !localRequirement &&
            zoneMapping[Z ?? ''] === 'SF'
          ) {
            connectBtnTrigger(true, { ...internetbarInitialValue });
          } else {
            setShowInternetBarModal(true);
            setModalType('LOGIN');
            setCurrentStateHasModalOption(true);
          }
          if (localRequirement === 'STRICT_AO') {
            setModalType('ACCESS');
          } else if (isNoLoginInterfaceModal) {
            connectBtnTrigger(isNoLoginInterfaceModal, { ...internetbarInitialValue });
          }
        } else if (zoneValue?.toUpperCase() === 'PUBLIC' || zoneValue?.toUpperCase() === 'LOBBY') {
          if (isUserEnrolled && ppv5Enrollment) {
            setShowInternetBarModal(true);
            setModalType('LOYALTY_WELCOME');
            setCurrentStateHasModalOption(true);
            setIsNoLoginInterfaceModal(false);
          } else {
            if (localRequirement && localRequirement !== 'TCTC') {
              if (localRequirement === 'AF_AO' || localRequirement === 'STRICT_AO') {
                setModalType('ACCESS');
                setShowInternetBarModal(true);
                setCurrentStateHasModalOption(true);
              } else {
                setModalType('LOGIN');
                setShowInternetBarModal(true);
                connectBtnTrigger(isNoLoginInterfaceModal, { ...internetbarInitialValue });
              }
            } else {
              setModalType('');
              setShowInternetBarModal(false);
              setLoyaltyConnectNowBtnLoading(true);
              connectBtnTrigger(true, { ...internetbarInitialValue });
            }
          }
        }
        break;
    }
  };

  useEffect(() => {
    if (serviceData?.connectBtnClick) {
      handleBtnClick('connectnow');
    }
  }, [serviceData?.connectBtnClick]);

  return currentStateMapping && pageProps ? (
    <div
      className={clsx(
        `container internet-bar-content ${
          enableLoyalty || (enableLoyaltyConference && isLoyaltyEnabledForConference)
            ? `internet-bar-content-${state}-loyalty`
            : `internet-bar-content-${state}`
        }`,
        `${stateCSS?.internetBarContent}`
      )}
    >
      <div
        className={clsx(
          'col-12 d-flex flex-column justify-content-center px-0 text-center align-items-center',
          `${stateCSS?.internetBarContentContainer}`
        )}
      >
        {/* 1. Internet Bar Icon Configuration */}
        {showIcon && (
          <span
            className={clsx(
              `t-accent-color d-inline-block ${internetBarIcons?.[state]?.iconName} internet-bar-content__icon internet-bar-content-${state}__icon`,
              `${stateCSS?.internetBarIcon}`
            )}
            data-testid="internet-bar-icon"
          ></span>
        )}
        {/* 2. Title */}
        {isWelcomeTitle ? (
          <>
            <h3
              className={`mt-4 mb-3 pt-xl-4 internet-bar-content__title internet-bar-content-${state}__title`}
              data-testid="internet-bar-welcome-title"
            >
              {pageProps?.[currentStateMapping?.title as keyof AEMInternetBarProps] || ''}
            </h3>
            {/* Separator */}
            <div
              className={`t-accent-color mb-3 mb-xl-4 internet-bar-content__separator internet-bar-content-${state}__separator`}
            ></div>
          </>
        ) : (
          <h3
            className={clsx('col-12 internet-bar-content__title', `${stateCSS?.internetBarNotWelcomeTitle}`)}
            data-testid="internet-bar-welcome-title"
          >
            {pageProps?.[currentStateMapping?.title as keyof AEMInternetBarProps] ?? ''}
          </h3>
        )}

        <div className={clsx('loyalty-container', `${stateCSS?.internetBarLoyaltyContainer}`)}>
          <div
            className={clsx(
              'd-flex mb-5',
              `${loyaltyFlip ? 'flex-column-reverse flex-md-row-reverse' : 'flex-column flex-md-row'}`
            )}
          >
            <div className="loyalty-container-card1 col-12 col-md-6 p-0 d-flex flex-column justify-content-between align-items-center">
              <div className={clsx('d-flex flex-column', `${loyaltyFlip ? 'pt-5 pt-md-0' : ''}`)}>
                <p
                  className={clsx(
                    't-font-s mb-0 loyalty-description t-font-weight-m pb-2',
                    `${stateCSS?.internetBarLoyaltyDescription}`
                  )}
                >
                  {pageProps?.loyaltyJoinnowScenarioText}
                </p>
                <p className={clsx('t-font-s', `${stateCSS?.internetBarLoyaltySubDescription}`)}>
                  {pageProps?.loyaltyJoinnowScenarioDescription}
                </p>
              </div>
              <Button
                className={clsx(
                  'm-button-primary pt-2 loyalty-buttons',
                  `${stateCSS?.internetBarLoyaltyButtons} d-flex justify-content-center`,
                  `${!pageProps?.enableLoyaltyJoinNowBtn ? 'disabled' : ''}`
                )}
                isLink={true}
                href={loyaltyJoinNow}
                trackingProperties={pageProps?.trackingProperties}
                target={pageProps?.joinButtonLinkInNewTab}
                custom_click_track_value={`Internet Bar|${pageProps?.joinButtonLabel}|external`}
                data-custom_click_track_value={`Internet Bar|${pageProps?.joinButtonLabel}|external`}
                data-is-click-track={true}
                ariaLabel={pageProps?.joinButtonLabel}
                isDisabled={pageProps?.isAuthorMode ? false : !pageProps?.enableLoyaltyJoinNowBtn}
              >
                {!pageProps?.enableLoyaltyJoinNowBtn ? (
                  <div className="m-spinner-s" data-testid="activate-spinner"></div>
                ) : (
                  <span>{pageProps?.joinButtonLabel}</span>
                )}
              </Button>
            </div>
            <div className="loyalty-container-card2 col-12 col-md-6 d-flex flex-column justify-content-between align-items-center">
              <div
                className={`${
                  loyaltyFlip ? '' : 'd-flex flex-column justify-content-between align-items-center flex-1 pt-5 pt-md-0'
                }`}
              >
                <p
                  className={clsx(
                    't-font-s mb-0 loyalty-description t-font-weight-m pb-2',
                    `${stateCSS?.internetBarLoyaltyDescription}`
                  )}
                >
                  {loyaltyConnectScenarioTextLabel}
                </p>
                <p className={clsx('t-font-s', `${stateCSS?.internetBarLoyaltySubDescription}`)}>
                  {loyaltyConnectScenarioDescriptionLabel}
                </p>
                <Button
                  className={clsx(
                    'm-button-primary loyalty-buttons',
                    `${stateCSS?.internetBarLoyaltyButtons}`,
                    `${isLoyaltyConnectNowBtnLoading ? 'disabled' : ''}`
                  )}
                  isDisabled={isLoyaltyConnectNowBtnLoading}
                  trackingProperties={pageProps?.trackingProperties}
                  data-custom_click_track_value={`Internet Bar|${pageProps?.loyaltyConnectNowButton}|internal`}
                  custom_click_track_value={`Internet Bar|${pageProps?.loyaltyConnectNowButton}|internal`}
                  data-is-click-track={true}
                  ariaLabel={pageProps?.loyaltyConnectNowButton}
                  callback={() => {
                    handleBtnClick('connectnow');
                  }}
                >
                  {isLoyaltyConnectNowBtnLoading ? (
                    <div className="m-spinner-s" data-testid="activate-spinner"></div>
                  ) : (
                    <span>{pageProps?.loyaltyConnectNowButton}</span>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {currentStateMapping?.termsAndConditions && (
            <p
              className={clsx(
                `mb-0 t-font-xs internet-bar-content__footer-container__tnc-text`,
                `${stateCSS?.internetBarTermsAndConditions}`
              )}
              data-testid="internet-bar-terms-condition"
              dangerouslySetInnerHTML={{
                __html: termsAndConditionsText,
              }}
            ></p>
          )}
        </div>
      </div>
    </div>
  ) : null;
};
